import { useMemo, useState } from 'react';
import { useEffect } from 'react';

import UserService from '@/services/user.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';

import './styles/CancelOrderModal.css';

const REASONS = [
  "I’ve decided not to proceed",
  "I found a different artwork",
  "The delivery timeline doesn’t work for me",
  "Other (please specify)",
];

export default function CancelOrderModal({
  open,
  onClose,
  orderNumber,
  onCancelled,
}) {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setDetails("");
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const detailsRequired = useMemo(
    () => reason.toLowerCase().startsWith("other"),
    [reason],
  );
  const trimmedReason = reason.trim();
  const trimmedDetails = details.trim();

  const handleSubmit = async () => {
    if (submitting) {
      return;
    }
    const normalizedReason = trimmedReason;
    const normalizedDetails = trimmedDetails;
    if (!normalizedReason) {
      setError("Please select a reason to continue.");
      return;
    }
    if (detailsRequired && !normalizedDetails) {
      setError("Please share a few details so we can help.");
      return;
    }
    if (detailsRequired && normalizedDetails.length < 20) {
      setError("Please provide at least 20 characters.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        reason: normalizedReason,
        details: normalizedDetails || undefined,
      };
      const { response, data } = await UserService.cancelOrder(orderNumber, payload);
      if (!response?.ok) {
        throw new Error(
          data?.error_message
          ?? data?.message
          ?? "We couldn't cancel this order. Please try again."
        );
      }
      if (typeof onCancelled === "function") {
        onCancelled(data);
      }
      if (typeof onClose === "function") {
        onClose();
      }
    } catch (err) {
      setError(err?.message ?? "We couldn't cancel this order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) {
      return;
    }
    if (typeof onClose === "function") {
      onClose();
    }
  };

  return (
    <GenericPopup
      open={open}
      className="cancel-order-modal"
      backgroundClassName="cancel-order-modal__backdrop"
      opacity
      onBackgroundClick={handleClose}
    >
      <div className="cancel-order-modal__body">
        <h3 className="cancel-order-modal__title">Cancel this order?</h3>
        <p className="cancel-order-modal__description">
          You can cancel within 24 hours of payment authorization. If you cancel, this purchase will be reversed, the artwork will be removed from your collection, and it will be released back for sale.
        </p>
        <p className="cancel-order-modal__description">
          If your payment has not been captured yet, the authorization will be voided and you won’t be charged. If it has been captured, we’ll issue a full refund to your original payment method (timing depends on your bank).
        </p>

        <label className="cancel-order-modal__label" htmlFor="cancel-reason">
          Reason for cancellation (required)
        </label>
        <select
          id="cancel-reason"
          className="cancel-order-modal__select"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        >
          <option value="">Select a reason</option>
          {REASONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

        <label className="cancel-order-modal__label" htmlFor="cancel-details">
          Additional details
        </label>
        <textarea
          id="cancel-details"
          className="cancel-order-modal__textarea"
          placeholder="Optional notes to help us understand your decision…"
          value={details}
          onChange={(event) => setDetails(event.target.value)}
          rows={3}
        />

        {error ? <p className="cancel-order-modal__error">{error}</p> : null}

        <div className="cancel-order-modal__actions">
          <ContinueButton
            className="cancel-order-modal__primary"
            onClick={handleClose}
            turnOff={submitting}
          >
            Keep artwork
          </ContinueButton>
          <ContinueButton
            className="cancel-order-modal__secondary"
            loading={submitting}
            turnOff={
              submitting
              || !trimmedReason
              || (detailsRequired && trimmedDetails.length < 20)
            }
            onClick={handleSubmit}
          >
            Cancel purchase
          </ContinueButton>
        </div>
      </div>
    </GenericPopup>
  );
}
